import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Grid, Typography, Divider, IconButton } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LogoImg from '../../assets/AcoSignFavIcon.png';

const Footer = () => {
  const navigate = useNavigate();

  const handleNav = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#070D19',
        color: '#FFFFFF',
        pt: { xs: 8, md: 10 },
        pb: 5,
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle Background Glow */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37, 99, 235, 0.12) 0%, rgba(0, 0, 0, 0) 70%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 6 }} sx={{ mb: 8 }}>
          {/* Column 1: Brand & Tagline */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                component="img"
                src={LogoImg}
                alt="Coalitionify Logo"
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  objectFit: 'contain',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em' }}>
                Coalitionify Innovate
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#94A3B8', mb: 3, maxWidth: 320, lineHeight: 1.7 }}>
              Building smarter business experiences through Software, AI and Automation.
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '0.8rem' }}>
              Coalitionify Innovate Private Limited
            </Typography>
          </Grid>

          {/* Column 2: Quick Links */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2.5, fontSize: '0.8rem' }}
            >
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[
                { label: 'Home', path: '/' },
                { label: 'About Us', path: '/about' },
                { label: 'Products & Solutions', path: '/products' },
                { label: 'Blog & Insights', path: '/blog' },
                { label: 'Contact Us', path: '/contact' },
              ].map((item) => (
                <Typography
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  sx={{
                    color: '#94A3B8',
                    fontSize: '0.925rem',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: '#60A5FA' },
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Column 3: Our Solutions */}
          <Grid item xs={6} sm={4} md={3}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2.5, fontSize: '0.8rem' }}
            >
              Our Solutions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[
                'Acolead CRM',
                'Instagram & Facebook AI',
                'WhatsApp Business API & AI',
                'AI Calling Solutions',
              ].map((sol) => (
                <Typography
                  key={sol}
                  onClick={() => handleNav('/products')}
                  sx={{
                    color: '#94A3B8',
                    fontSize: '0.925rem',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: '#60A5FA' },
                  }}
                >
                  {sol}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Column 4: Connect & Legal */}
          <Grid item xs={12} sm={5} md={3}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2.5, fontSize: '0.8rem' }}
            >
              Connect
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
              {[
                { icon: <LinkedInIcon fontSize="small" />, href: 'https://www.linkedin.com/company/coalitionify-innovate/posts/?feedView=all', label: 'LinkedIn' },
                { icon: <InstagramIcon fontSize="small" />, href: 'https://instagram.com', label: 'Instagram' },
                { icon: <FacebookIcon fontSize="small" />, href: 'https://facebook.com', label: 'Facebook' },
              ].map((s, idx) => (
                <IconButton
                  key={idx}
                  component="a"
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  sx={{
                    color: '#94A3B8',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: '#2563EB',
                      color: '#FFFFFF',
                      borderColor: '#2563EB',
                    },
                  }}
                >
                  {s.icon}
                </IconButton>
              ))}
            </Box>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5, fontSize: '0.8rem' }}
            >
              Legal
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography
                onClick={() => handleNav('/privacy')}
                sx={{
                  color: '#94A3B8',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  '&:hover': { color: '#60A5FA' },
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                onClick={() => handleNav('/terms-conditions')}
                sx={{
                  color: '#94A3B8',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  '&:hover': { color: '#60A5FA' },
                }}
              >
                Terms & Conditions
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', mb: 4 }} />

        {/* Footer Bottom Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem' }}>
            © 2026 Coalitionify Innovate Private Limited. All Rights Reserved.
          </Typography>
          <Typography variant="caption" sx={{ color: '#475569', fontSize: '0.8rem' }}>
            Intelligent Technology for Modern Businesses
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
